import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { TextUtils } from '@mira-avant/mtlibs';
import { ValidationUtil } from '../../../../utils/validation.util';

@Component({
	//It's an angular requirement to define the template even if it's empty.
	template: '',
})
export class BaseControlComponent {
	@Output('onChange') public onChangeEmitter: EventEmitter<unknown> = new EventEmitter();
	@Output('onBlur') public onBlurEmitter: EventEmitter<unknown> = new EventEmitter();
	@Input('fg') public formGroup: FormGroup | undefined;
	@Input('formGroupOrControlName') public formGroupOrControlName: string = '';
	@Input('label') public label: string | undefined;
	@Input('placeholder') public placeholder: string | undefined;
	@Input('noLabel') public noLabel: boolean = false;

	//This function will generate the next most interesting validation hint for
	//this control.
	public curateValidationHint: any = ValidationUtil.curateValidationHint;

	/**
	 * If a nested form group like phone number or date range
	 * inherits from this component it can access a correctly typed
	 * reference to its inner form group.
	 * @date 7/14/2022 - 2:31:14 PM
	 *
	 * @public
	 * @returns {(FormGroup | undefined)}
	 */
	public getInnerGroup(): FormGroup | undefined {
		return this.formGroup?.get(this.formGroupOrControlName) as FormGroup;
	}

	/**
	 * If this is a nested form we may need an array of the inner controls
	 * such as with a multi-select-checkbox control.
	 * @date 7/15/2022 - 9:41:59 AM
	 *
	 * @public
	 * @returns {FormControl[]}
	 */
	public getInnerControls(): FormControl[] {
		//The controls is an object where the key is the name of the control
		//and the value of the control is the control itself.
		const innerGroupControls: any = this.getInnerGroup()?.controls || {};

		//Get the keys so we can convert the object to an array
		const innerGroupControlKeys: string[] = Object.keys(innerGroupControls);

		//Convert the object to an array. We want the array so we can do an *ngFor
		//in the template to render the set of controls.
		return innerGroupControlKeys.map((key: string) => innerGroupControls[key]);
	}

	/**
	 * Send event up to listeners.
	 * @date 2/20/2024 - 12:38:21 PM
	 *
	 * @public
	 * @param {unknown} $event
	 */
	public onChange($event: unknown): void {
		this.onChangeEmitter.emit($event);
	}

	/**
	 * Return the string names of the inner controls
	 * @date 7/15/2022 - 9:46:37 AM
	 *
	 * @public
	 * @returns {string[]}
	 */
	public getInnerControlNames(): string[] {
		//The controls is an object where the key is the name of the control
		//and the value of the control is the control itself.
		const innerGroupControls: any = this.getInnerGroup()?.controls || {};

		//Return the keys which is the control names.
		return Object.keys(innerGroupControls);
	}

	/**
	 * Return the form control in a typesafe way.
	 * @date 7/15/2022 - 9:22:07 AM
	 *
	 * @public
	 * @returns {(FormControl | undefined)}
	 */
	public getControl(): FormControl | undefined {
		return this.formGroup?.get(this.formGroupOrControlName) as FormControl;
	}

	/**
	 * Generate a default label if one isn't explicitly provided.
	 * @date 6/7/2022 - 5:58:13 PM
	 *
	 * @public
	 * @returns {string}
	 */
	public getLabel(): string {
		//Return the explicit label if provided
		if (this.label) {
			return this.label;
		}

		//If it is not provided we will convert the form control's name
		//to a title case label...but we need the name
		if (!this.formGroupOrControlName) {
			throw new Error('No name specified for form control.');
		}

		return TextUtils.convertCamelCaseToTitleCase(this.formGroupOrControlName);
	}
}
